import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Share2, Loader2, Copy, X, ChevronDown, Moon, Sun } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/adminLang";
import { SCEN, kompakt, prodLabel } from "./budsjettUtils";
import Overview from "./Overview";
import Revenue from "./Revenue";
import Costs from "./Costs";
import CashFlow from "./CashFlow";
import Scenarios from "./Scenarios";

const SUBS = {
  overview: { label: "Overview", desc: "Executive summary — forecast, assumptions, break-even" },
  revenue: { label: "Revenue", desc: "Four engines — physical, digital, dinner, partners" },
  costs: { label: "Costs", desc: "Driver-based cost lines · quote → contract → invoice" },
  cashflow: { label: "Cash Flow", desc: "Monthly liquidity, payments incl. VAT" },
  scenarios: { label: "Scenarios", desc: "Downside · Base · Upside" },
};

/* ── Del-modal: én stabil offentlig lenke med passord (som Playbook) ── */
function DelModal({ onLukk, t }) {
  const [lenke, setLenke] = useState(null);
  const [laster, setLaster] = useState(true);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    api.post("/admin/budget/share")
      .then((r) => setLenke(r.data.link))
      .catch(() => toast.error(t("Something went wrong")))
      .finally(() => setLaster(false));
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk, t]);

  const url = lenke ? `${window.location.origin}/budget/s/${lenke.token}` : "";
  const kopier = (tekst, hva) => {
    navigator.clipboard?.writeText(tekst).then(
      () => toast.success(`${hva} ${t("copied")}`),
      () => toast.error(t("Something went wrong"))
    );
  };
  const toggleAktiv = async () => {
    try {
      const r = await api.patch("/admin/budget/share", { active: !lenke.active });
      setLenke(r.data.link);
      toast.success(r.data.link.active ? t("Link activated") : t("Link deactivated"));
    } catch { toast.error(t("Something went wrong")); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[14vh] px-4" data-testid="budget-del-modal">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <div className="sak-modal relative w-full max-w-[520px] bg-white rounded-2xl shadow-2xl border border-[#E7E4DB] p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-[15px] font-semibold">{t("External link")}</h2>
          <button type="button" onClick={onLukk} className="text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="budget-del-lukk">
            <X size={16} />
          </button>
        </div>
        <p className="text-[12px] text-neutral-500 mb-5">{t("Public, password-protected budget memo (read-only, no sponsor names). Share the link and password separately.")}</p>
        {laster ? (
          <p className="text-[12.5px] text-neutral-400">{t("Loading …")}</p>
        ) : lenke ? (
          <div className="grid gap-4">
            <div>
              <p className="pb-mono text-[9px] uppercase tracking-[0.22em] text-neutral-400 mb-1.5">{t("Link")}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-[12px] text-neutral-800 border border-neutral-200 rounded-lg px-3 py-2.5" data-testid="budget-del-url">{url}</code>
                <button type="button" className="adm-btn-ghost adm-btn-sm shrink-0" onClick={() => kopier(url, t("Link"))} data-testid="budget-del-kopier-url"><Copy size={12} /></button>
              </div>
            </div>
            <div>
              <p className="pb-mono text-[9px] uppercase tracking-[0.22em] text-neutral-400 mb-1.5">{t("Password")}</p>
              <div className="flex items-center gap-2">
                <code className="pb-mono text-[16px] tracking-[0.3em] font-semibold text-neutral-900 border border-neutral-200 rounded-lg px-4 py-2" data-testid="budget-del-kode">{lenke.passcode}</code>
                <button type="button" className="adm-btn-ghost adm-btn-sm shrink-0" onClick={() => kopier(lenke.passcode, t("Password"))} data-testid="budget-del-kopier-kode"><Copy size={12} /></button>
                <span className={`ml-auto pb-mono text-[9.5px] uppercase tracking-[0.14em] ${lenke.active ? "text-emerald-600" : "text-red-500"}`}>
                  {lenke.active ? t("Active") : t("Deactivated")}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
              <p className="text-[11px] text-neutral-400">{t("The link is stable — deactivate to close access immediately.")}</p>
              <button type="button" className={lenke.active ? "adm-btn-danger" : "adm-btn"} onClick={toggleAktiv} data-testid="budget-del-toggle">
                {lenke.active ? t("Deactivate") : t("Activate")}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-[12.5px] text-red-500">{t("Something went wrong")}</p>
        )}
      </div>
    </div>
  );
}

export default function BudsjettAdmin() {
  const { sub } = useParams();
  const { t } = useLang();
  const page = SUBS[sub] ? sub : "overview";
  const [data, setData] = useState(null);
  const [scen, setScen] = useState(() => {
    const v = localStorage.getItem("nas_budget_scen");
    return SCEN.some((s) => s.k === v) ? v : "base";
  });
  const [delModal, setDelModal] = useState(false);
  const [eksportMeny, setEksportMeny] = useState(false);
  const [pdfLaster, setPdfLaster] = useState(false);
  const aktor = localStorage.getItem("nas_aktor") || "Admin";

  const fail = useCallback((e) => toast.error(formatApiError(e?.response?.data?.detail) || t("Something went wrong")), [t]);

  const velgScen = (k) => {
    localStorage.setItem("nas_budget_scen", k);
    setScen(k);
  };

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/budget");
      setData(d);
    } catch (e) {
      fail(e);
    }
  }, [fail]);

  useEffect(() => { load(); }, [load]);

  const kall = useCallback(async (fn, melding) => {
    try {
      await fn();
      await load();
      if (melding) toast.success(melding);
    } catch (e) {
      fail(e);
      throw e;
    }
  }, [load, fail]);

  const actions = {
    aktor,
    patchSettings: (p) => kall(() => api.patch("/admin/budget/settings", { ...p, actor: aktor })),
    patchScenario: (key, p) => kall(() => api.patch(`/admin/budget/scenarios/${key}`, { ...p, actor: aktor })),
    newTicket: (p) => kall(() => api.post("/admin/budget/tickets", { ...p, actor: aktor }), t("Ticket product created")),
    patchTicket: (id, p) => kall(() => api.patch(`/admin/budget/tickets/${id}`, { ...p, actor: aktor })),
    deleteTicket: (id) => kall(() => api.delete(`/admin/budget/tickets/${id}`), t("Deleted")),
    newSponsor: (p) => kall(() => api.post("/admin/budget/sponsors", { ...p, actor: aktor }), t("Sponsor created")),
    patchSponsor: (id, p) => kall(() => api.patch(`/admin/budget/sponsors/${id}`, { ...p, actor: aktor })),
    deleteSponsor: (id) => kall(() => api.delete(`/admin/budget/sponsors/${id}`), t("Deleted")),
    newLine: (p) => kall(() => api.post("/admin/budget/lines", { ...p, actor: aktor }), t("Line created")),
    patchLine: (id, p) => kall(() => api.patch(`/admin/budget/lines/${id}`, { ...p, actor: aktor })),
    deleteLine: (id) => kall(() => api.delete(`/admin/budget/lines/${id}`), t("Deleted")),
  };

  if (!data) {
    return (
      <div data-testid="budget-admin">
        <p className="text-center text-xs text-neutral-400 py-20 adm-puls" data-testid="budget-loading">{t("Loading budget …")}</p>
      </div>
    );
  }

  const s = data.computed.scenarios[scen];
  const scenDoc = data.scenarios.find((x) => x.key === scen) || {};

  const lastNedPdf = async (tema) => {
    if (pdfLaster) return;
    setPdfLaster(true);
    try {
      const r = await api.get(`/admin/budget/pdf?theme=${tema}`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = tema === "light" ? "Norwegian-AI-Summit-2027-Budget-Light.pdf" : "Norwegian-AI-Summit-2027-Budget.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("PDF downloaded"));
    } catch (e) {
      fail(e);
    } finally {
      setPdfLaster(false);
    }
  };

  return (
    <div data-testid="budget-admin" className="max-w-[1240px] bud-x">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div>
          <p className="adm-eyebrow mb-1.5">{t("Budget")}</p>
          <h1 className="adm-title">{t(SUBS[page].label)}</h1>
          <p className="adm-sub mt-1.5">{t(SUBS[page].desc)}<span className="hidden md:inline"> · {t("NOK ex VAT · cash flow incl. VAT")}</span></p>
        </div>
        <div className="relative shrink-0" data-testid="budget-eksport">
          <button
            type="button"
            onClick={() => setEksportMeny((v) => !v)}
            disabled={pdfLaster}
            className="adm-btn-ghost adm-btn-sm"
            data-testid="budget-eksport-btn"
          >
            {pdfLaster
              ? <><Loader2 size={12} className="animate-spin" /> {t("Generating …")}</>
              : <><Share2 size={12} /> {t("Share")} · PDF <ChevronDown size={11} className="opacity-50" /></>}
          </button>
          {eksportMeny && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setEksportMeny(false)} />
              <div className="sak-modal absolute left-0 sm:left-auto sm:right-0 top-full mt-1.5 z-40 w-[248px] bg-white border border-[#E7E4DB] rounded-xl shadow-xl p-1.5" data-testid="budget-eksport-meny">
                <button
                  type="button"
                  onClick={() => { setEksportMeny(false); setDelModal(true); }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-neutral-50 transition-colors"
                  data-testid="budget-del-btn"
                >
                  <Share2 size={13} className="shrink-0 opacity-60" />
                  <span className="min-w-0">
                    <span className="block text-[12.5px] font-medium">{t("Share external link")}</span>
                    <span className="block text-[10.5px] opacity-50">{t("Password-protected memo")}</span>
                  </span>
                </button>
                <div className="my-1 border-t border-neutral-100" />
                <button
                  type="button"
                  onClick={() => { setEksportMeny(false); lastNedPdf("dark"); }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-neutral-50 transition-colors"
                  data-testid="budget-pdf-btn"
                >
                  <Moon size={13} className="shrink-0 opacity-60" />
                  <span className="min-w-0">
                    <span className="block text-[12.5px] font-medium">{t("PDF — dark")}</span>
                    <span className="block text-[10.5px] opacity-50">{t("Presentation style")}</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => { setEksportMeny(false); lastNedPdf("light"); }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-neutral-50 transition-colors"
                  data-testid="budget-pdf-light-btn"
                >
                  <Sun size={13} className="shrink-0 opacity-60" />
                  <span className="min-w-0">
                    <span className="block text-[12.5px] font-medium">{t("PDF — light")}</span>
                    <span className="block text-[10.5px] opacity-50">{t("Print-friendly · smaller file")}</span>
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      {/* Sticky scenario ticker — full-bleed hairline-strip */}
      <div className="sticky top-[50px] z-20 mb-8" data-testid="budget-scenbar">
        <div className="bud-ticker flex flex-wrap items-center gap-x-4 gap-y-0.5 py-0.5">
          <div className="flex items-center" data-testid="budget-scen-switch">
            {SCEN.map((sc) => (
              <button
                key={sc.k}
                type="button"
                onClick={() => velgScen(sc.k)}
                className={`bud-scen-btn ${scen === sc.k ? "aktiv" : ""}`}
                data-testid={`budget-scen-${sc.k}`}
              >
                <span className="dot" />
                {sc.label}
              </button>
            ))}
          </div>
          <p className="hidden lg:block ml-auto pr-2 text-[10.5px] uppercase tracking-[0.1em] text-neutral-400 tabular-nums truncate" data-testid="budget-scen-context">
            {s.physical.sold} {t("physical")} ({s.physical.sellThroughPct}%) · {s.digital.sold} {t("digital")} · {kompakt(s.revenue.sponsors)} {t("partners")} · {prodLabel(scenDoc.productionFactor)} {t("production")} · {s.dinner.guests} {t("dinner guests")}
          </p>
        </div>
      </div>

      {page === "overview" && <Overview data={data} scen={scen} s={s} actions={actions} />}
      {page === "revenue" && <Revenue data={data} scen={scen} actions={actions} />}
      {page === "costs" && <Costs data={data} scen={scen} actions={actions} />}
      {page === "cashflow" && <CashFlow data={data} scen={scen} s={s} actions={actions} />}
      {page === "scenarios" && <Scenarios data={data} actions={actions} />}

      {delModal && <DelModal onLukk={() => setDelModal(false)} t={t} />}
    </div>
  );
}
